<template>
  <nav id="header"
       class="navbar navbar-white bg-white shadow-sm"
       :style="[headerStyle, { position: 'sticky', top: 0, zIndex: 1030 }]"
       :class="isScrolled ? 'py-1' : 'py-0'">
    <div class="container-fluid px-0 d-flex align-items-center justify-content-end">
      <!-- Button group - pushed right with gap before image -->
      <div class="header-buttons me-3">
        <!-- Home button shown for focused sections (Sign In, Sign Up, My Appeal, Password Reset, etc.) -->
        <div v-if="isInFocusedSection()">
          <button @click="goHome()" class="btn btn-primary btn-sm">Home</button>
        </div>

        <!-- Home button shown for payment result pages -->
        <div v-else-if="isInPaymentResultMode()">
          <div class="btn-group">
            <button @click="returnToHome()" class="btn btn-primary btn-sm">Home</button>
            <!-- My Appeal and Download buttons only shown on success page -->
            <router-link v-if="paymentStore.paymentStatus === 'success'"
                         to="/my-appeal"
                         class="btn btn-sm btn-info">My Appeal</router-link>
            <button v-if="paymentStore.paymentStatus === 'success'"
                    @click="handleDownload()"
                    class="btn btn-purple btn-sm"
                    style="background-color: #6f42c1; color: white;">Download</button>
          </div>
        </div>

        <!-- Regular buttons shown on home page only -->
        <div v-else>
          <!-- Not signed in = My Appeal + Sign Up + Sign In -->
          <div v-if="!authStore.isSignedIn">
            <div class="btn-group">
              <router-link to="/my-appeal" class="btn btn-sm btn-info">My Appeal</router-link>
              <router-link to="/sign-up" class="btn btn-primary btn-sm">Sign Up</router-link>
              <router-link to="/sign-in" class="btn btn-sm btn-info">Sign In</router-link>
            </div>
          </div>

          <!-- Signed in but not purchased = My Appeal + Buy + Sign Out -->
          <div v-else-if="!paymentStore.hasPurchased">
            <div class="btn-group">
              <router-link to="/my-appeal" class="btn btn-sm btn-info">My Appeal</router-link>
              <button @click="handleBuyNow()" class="btn btn-primary btn-sm"
                      :disabled="paymentStore.isCheckingEligibility">
                {{ paymentStore.isCheckingEligibility ? '...' : 'Buy' }}
              </button>
              <button @click="handleSignOut()" class="btn btn-danger btn-sm">Sign Out</button>
            </div>
          </div>

          <!-- Signed in and purchased = My Appeal + Download + Sign Out  -->
          <div v-else>
            <div class="btn-group">
              <router-link to="/my-appeal" class="btn btn-sm btn-info">My Appeal</router-link>
              <button @click="handleDownload()" class="btn btn-purple btn-sm"
                      style="background-color: #6f42c1; color: white;">Download
              </button>
              <button @click="handleSignOut()" class="btn btn-danger btn-sm">Sign Out</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Logo flush to right edge -->
      <a class="navbar-brand mb-0 me-0 pe-0" href="#" @click.prevent="goHome">
        <img src="/static/images/header-with-qr-black.png"
             alt="HummingBird Logo"
             class="header-logo"
             :style="isScrolled ? 'height: 40px;' : 'height: 144px;'"
             @error="handleImageError">
      </a>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePaymentStore } from '@/stores/payment'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const paymentStore = usePaymentStore()

// Header scroll state
const isScrolled = ref(false)

// Fixed threshold with hysteresis to prevent oscillation
// HYSTERESIS must be > header height change (144px - 40px = 104px)
const SCROLL_THRESHOLD = 150  // Single threshold
const HYSTERESIS = 120         // Dead zone (larger than 104px height change)

// Header background style
const headerStyle = computed(() => ({
  background: "url('/static/images/header_content_background14.png') right center / cover no-repeat"
}))

// Check if in a focused section (Sign In, Sign Up, My Appeal, Password Reset pages)
// These pages show only a "Home" button instead of the full button set
function isInFocusedSection() {
  const focusedSections = ['SignIn', 'SignUp', 'MyAppeal', 'ForgotPassword', 'PasswordResetSent', 'ResetPassword', 'Contact']
  return focusedSections.includes(route.name)
}

// Check if in payment result mode
function isInPaymentResultMode() {
  return route.name === 'PaymentSuccess' || route.name === 'PaymentCancelled'
}

// Handle scroll for header shrinking with proper hysteresis
function handleScroll() {
  const currentScrollY = window.scrollY

  // Use hysteresis to prevent oscillation
  // When large: need to scroll DOWN past threshold to shrink
  // When small: need to scroll UP above (threshold - hysteresis) to expand

  if (!isScrolled.value && currentScrollY > SCROLL_THRESHOLD) {
    // Currently large, scrolled down past 150px -> shrink
    isScrolled.value = true
  } else if (isScrolled.value && currentScrollY < (SCROLL_THRESHOLD - HYSTERESIS)) {
    // Currently small, scrolled up above 30px -> expand
    isScrolled.value = false
  }
  // Between 30-150px: do nothing (dead zone prevents oscillation)
}

// Handle image load error
function handleImageError(event) {
  event.target.style.display = 'none'
}

// Go to home
function goHome() {
  router.push({ name: 'Home' })
  // Scroll to top after navigation
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, 100)
}

// Return to home
function returnToHome() {
  paymentStore.resetPaymentStatus()
  router.push({ name: 'Home' })
}

// Handle sign out
async function handleSignOut() {
  await authStore.signOut()
  router.push({ name: 'Home' })
}

// Handle buy now (with eligibility check)
async function handleBuyNow() {
  try {
    const eligible = await paymentStore.checkEligibility()
    if (eligible) {
      await paymentStore.createCheckout()
    } else {
      router.push({ name: 'EligibilityWarning' })
    }
  } catch (error) {
    console.error('Failed to create checkout:', error)
    alert('Failed to start checkout. Please try again.')
  }
}

// Handle download
async function handleDownload() {
  try {
    await paymentStore.downloadPDF()
  } catch (error) {
    console.error('Failed to download PDF:', error)

    // Show specific error message from backend
    let errorMessage = 'Failed to download PDF. Please try again.'
    if (error.response?.data?.detail) {
      errorMessage = error.response.data.detail
    } else if (error.response?.status === 401) {
      errorMessage = 'Please sign in to download'
    } else if (error.response?.status === 403) {
      errorMessage = 'Please purchase the appeal pack first'
    } else if (error.response?.status === 404) {
      errorMessage = 'PDF not found. Please contact support.'
    }

    alert(errorMessage)
  }
}

// Set initial scroll state on mount
onMounted(() => {
  isScrolled.value = window.scrollY > SCROLL_THRESHOLD
  window.addEventListener('scroll', handleScroll, { passive: true })
})

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
/* ------------------------------------------------------------ */
/* Header - Smooth Shrinking                                   */
/* ------------------------------------------------------------ */

/* Base transition for the header container */
#header {
  transition: all 0.35s ease;
}

/* Logo styling and transition: Crucial for vertical shrink */
.header-logo {
  height: 144px; /* Default large height */
  width: auto;
  object-fit: contain;
  transition: height 0.5s ease; /* Enables smooth height transition */
}

/* Buttons stay visible */
.btn-group {
  white-space: nowrap;
}

/* Ensure logo stays flush right with no extra margin */
.navbar-brand {
  flex-shrink: 0;
  margin-right: 0 !important;
  padding-right: 0 !important;
}
</style>
